function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function triangle01(t) {
  const m = ((t % 1) + 1) % 1;
  return m < 0.5 ? m * 2 : (1 - m) * 2;
}

export const kinematics2dAnim = {
  eval({ time, params }) {
    const type = String(params?.type || '').toLowerCase();
    const durationSec = Number.isFinite(params?.durationSec)
      ? Math.max(0.1, params.durationSec)
      : 4;
    const loop = params?.loop === true;
    const t = loop ? time % durationSec : Math.min(time, durationSec);
    const done = !loop && time >= durationSec;
    const props = {};

    if (type === 'free_fall') {
      const y0 = Number.isFinite(params?.y0k) ? params.y0k : 0.15;
      const v0 = Number.isFinite(params?.v0kPerSec) ? params.v0kPerSec : 0;
      const g = Number.isFinite(params?.gkPerSec2) ? params.gkPerSec2 : 0.28;
      const yMax = Number.isFinite(params?.yMaxk) ? params.yMaxk : 0.88;
      props.yk = clamp(y0 + v0 * t + 0.5 * g * t * t, 0, yMax);
      return { props, done };
    }

    if (type === 'bounce_ball') {
      const y0 = Number.isFinite(params?.y0k) ? params.y0k : 0.2;
      const v0 = Number.isFinite(params?.v0kPerSec) ? params.v0kPerSec : 0;
      const g = Number.isFinite(params?.gkPerSec2) ? params.gkPerSec2 : 0.34;
      const floor = Number.isFinite(params?.floorYk) ? params.floorYk : 0.9;
      const restitution = Number.isFinite(params?.restitution)
        ? clamp(params.restitution, 0, 0.98)
        : 0.72;
      const maxBounces = Number.isFinite(params?.maxBounces) ? Math.max(1, params.maxBounces) : 12;

      let y = y0;
      let v = v0;
      let bounces = 0;
      const dt = 1 / 120;
      const simT = Math.max(0, t);
      const steps = Math.min(2400, Math.ceil(simT / dt));

      for (let i = 0; i < steps; i++) {
        const step = i === steps - 1 ? simT - dt * (steps - 1) : dt;
        v += g * step;
        y += v * step;

        if (y >= floor) {
          y = floor;
          v = -Math.abs(v) * restitution;
          bounces += 1;
          if (bounces >= maxBounces && Math.abs(v) < 0.02) {
            v = 0;
          }
        }
      }

      props.yk = clamp(y, 0, floor);
      return { props, done };
    }

    if (type === 'light_pulse') {
      const x1 = Number.isFinite(params?.x1k) ? params.x1k : 0.74;
      const y1 = Number.isFinite(params?.y1k) ? params.y1k : 0.18;
      const x2 = Number.isFinite(params?.x2k) ? params.x2k : 0.9;
      const y2 = Number.isFinite(params?.y2k) ? params.y2k : 0.78;
      const cyclesPerSec = Number.isFinite(params?.cyclesPerSec)
        ? Math.max(0.05, params.cyclesPerSec)
        : 0.8;
      const p = triangle01(t * cyclesPerSec);
      props.xk = x1 + (x2 - x1) * p;
      props.yk = y1 + (y2 - y1) * p;
      return { props, done };
    }

    if (type === 'wavefront_expand') {
      const minR = Number.isFinite(params?.minRk) ? Math.max(0, params.minRk) : 0.01;
      const maxR = Number.isFinite(params?.maxRk) ? Math.max(minR, params.maxRk) : 0.2;
      const expandSec = Number.isFinite(params?.expandSec) ? Math.max(0.1, params.expandSec) : 2.5;
      const phase = loop ? (t % expandSec) / expandSec : clamp(t / expandSec, 0, 1);
      props.rk = minR + (maxR - minR) * phase;
      props.alpha = Number.isFinite(params?.alphaBase)
        ? clamp(params.alphaBase * (1 - phase), 0, 1)
        : clamp(1 - phase, 0.08, 1);
      return { props, done };
    }

    if (type === 'pendulum_swing') {
      const base = Number.isFinite(params?.baseAngleRad) ? params.baseAngleRad : 0;
      const amp = Number.isFinite(params?.amplitudeRad) ? params.amplitudeRad : 0.55;
      const omega = Number.isFinite(params?.omega) ? params.omega : 2.6;
      const phase = Number.isFinite(params?.phaseRad) ? params.phaseRad : 0;
      props.angleRad = base + amp * Math.sin(omega * t + phase);
      return { props, done };
    }

    if (type === 'spring_oscillation') {
      const x0 = Number.isFinite(params?.x2BaseK) ? params.x2BaseK : 0.62;
      const amp = Number.isFinite(params?.amplitudeK) ? params.amplitudeK : 0.06;
      const omega = Number.isFinite(params?.omega) ? params.omega : 6;
      const damping = Number.isFinite(params?.damping) ? Math.max(0, params.damping) : 0.25;
      props.x2k = x0 + amp * Math.exp(-damping * t) * Math.sin(omega * t);
      return { props, done };
    }

    return { props: {}, done };
  },
};
